import type { ReactNode } from 'react';
import { CoinIcon } from './icons';

export interface SwagCardProps {
  /** Image slot — use SmartImage (its default `maxVariant="card"` is the correct cap for this list slot). */
  image: ReactNode;
  name: string;
  /** Price in bucks. */
  price: number;
  /** Accessible price text, e.g. `800 CloudBucks`. */
  priceLabel: string;
  /** Stock caption, e.g. t('ec2.shop.stockLow', {count: 3}) — rendered in --warning. */
  stockCaption?: string;
  /**
   * Shown when the viewer can't afford it, e.g. t('ec2.shop.needMore', {amount: 450}).
   * Brief: full color, caption in --fg-muted — never grey the dream.
   */
  insufficientCaption?: string;
  onClick?: () => void;
  locale?: string;
  className?: string;
}

/** Shop swag card (brief §6): photo, subheading name, gold numeric-lg price + coin. */
export function SwagCard({
  image,
  name,
  price,
  priceLabel,
  stockCaption,
  insufficientCaption,
  onClick,
  locale = 'en',
  className = '',
}: SwagCardProps) {
  const Tag: 'button' | 'article' = onClick ? 'button' : 'article';
  const tagProps = onClick ? ({ type: 'button', onClick } as const) : ({} as const);
  const formatted = new Intl.NumberFormat(locale).format(price);
  return (
    <Tag
      {...tagProps}
      data-testid="ec-swag-card"
      className={
        `flex w-full flex-col overflow-hidden rounded-(--radius-card) border border-border bg-surface text-left ` +
        `focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${className}`
      }
    >
      <div className="aspect-square w-full overflow-hidden bg-surface-overlay [&_img]:h-full [&_img]:w-full [&_img]:object-cover">
        {image}
      </div>
      <div className="flex flex-col gap-1 p-3">
        <h3 className="truncate text-[17px] font-semibold text-fg">{name}</h3>
        <span role="img" aria-label={priceLabel} className="inline-flex items-center gap-1.5 font-display text-2xl leading-none font-[750] tabular-nums text-bucks">
          <CoinIcon size={16} aria-hidden />
          <span aria-hidden>{formatted}</span>
        </span>
        {stockCaption && <p className="text-[13px] font-medium text-warning">{stockCaption}</p>}
        {insufficientCaption && <p className="text-[13px] font-medium text-fg-muted">{insufficientCaption}</p>}
      </div>
    </Tag>
  );
}
